import { isMapEditing } from './editlock';
// 3D sensor coverage: a MapLibre custom WebGL layer rendering radar detection
// shells plus the older, explicitly nominal engagement volumes.
//
// Radar profiles come from the pure model in radar.ts: standard-atmosphere
// effective-Earth horizon, sensor altitude, min/max elevation, true slant range,
// target ceiling, and optional RCS/reference-range scaling. Ground radars gain a
// rising low-altitude horizon skirt and rounded far boundary; airborne/aerostat
// sensors gain look-down coverage and a sensor-anchored nadir/zenith field of
// regard. A fixed face still narrows the shell to its azimuth sector.
//
// The WebGL mesh is only a sampled display of that evaluator. It is never used
// for detection decisions.
//
// Interaction is via a pickable footprint layer under the domes (custom WebGL
// isn't natively pickable): hover for details + highlight, click to open the
// system, shift-click to isolate. Detection vs engagement visibility toggles
// independently. (Mercator projection; the domes are for the tilted view.)

import * as maplibregl from 'maplibre-gl';
import type { EntityDto } from './api';
import { fetchSiteElevationKm } from './elevation';
import { registerHover } from './hover';
import { geodesicCircle, geodesicSector, getRegistry, radarModelOf,
         rangeKmOf } from './objects';
import { coverageShapeProfile, groundReachAtAltitudeKm,
         normalizeBearing } from './radar';

const LAYER_ID = 'ad-domes';
const PICK_SRC = 'dome-pick';
const PICK_FILL = 'dome-pick-fill';
const DEFAULT_CEILING_KM = 30;

// The radar shell is an analytic phased-array lobe (radar.ts coverageShapeProfile):
// apex + one outer arc + apex = ARC_SAMPLES + 3 cross-section points, hence
// ARC_SAMPLES + 2 topology intervals. Engagement ellipsoids reuse the same
// topology. Kept modest — a smooth lobe doesn't need a dense cage.
const COVERAGE_ARC_SAMPLES = 20;
const RINGS = COVERAGE_ARC_SAMPLES + 2;
const SEGS = 40;
// Sparse wireframe: a few silhouette meridians and range arcs, not a line on
// every quad — the old full cage was thousands of segments of visual noise.
const GRID_MERIDIANS = 6;
const GRID_LAT_ARCS = 4;
// Where the modeled slant range peaks and how fast it tapers (illustrative
// fixed-face-array choices, not vendor data). Range/elevation/azimuth limits
// come from the entity's spec; these two shape the lobe's vertical falloff.
const RANGE_PEAK_ELEV_DEG = 20;
const RANGE_ELEV_EXPONENT = 1.4;
// Coverage fades out just above the horizon, so the lobe lifts off the ground
// (a low-altitude blind wedge) instead of lying flat on it. No spec carries a
// min elevation today, so this floor is what creates that gap.
const LOW_FADE_ELEV_DEG = 1;
// Detection (radar) and engagement (weapon) volumes are drawn in distinct,
// configurable looks so the two overlapping shells read apart — cyan for what
// the radar sees, red for where it engages, by default. Admin sets the default;
// each user can override in their browser (setVolumeStyle). The flat map markers
// and 2D rings still carry each system's own type colour.
export interface VolumeStyle {
  color: string;
  lineOpacity: number;   // 0..1 — grid/wireframe alpha
  fill: boolean;         // draw the translucent shell fill at all
  fillOpacity: number;   // 0..1 — shell fill alpha
  lineWidth: number;     // px, best-effort (GPUs cap gl.lineWidth, often at 1)
}
export type VolumeKind = 'radar' | 'engage';
const volumeStyle: Record<VolumeKind, VolumeStyle> = {
  radar:  { color: '#38bdf8', lineOpacity: 0.42, fill: true, fillOpacity: 0.04, lineWidth: 1 },
  engage: { color: '#f4514e', lineOpacity: 0.5, fill: true, fillOpacity: 0.16, lineWidth: 1 },
};
export function setVolumeStyle(
    patch: Partial<Record<VolumeKind, Partial<VolumeStyle>>>): void {
  if (patch.radar) Object.assign(volumeStyle.radar, patch.radar);
  if (patch.engage) Object.assign(volumeStyle.engage, patch.engage);
}
export function getVolumeStyle(): Record<VolumeKind, VolumeStyle> {
  return volumeStyle;
}
const VPD = (RINGS + 1) * (SEGS + 1);   // vertices per dome

// --- helpers ----------------------------------------------------------------

function hexRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [0.33, 0.84, 0.75];
  const n = parseInt(m[1]!, 16);
  return [(n >> 16 & 255) / 255, (n >> 8 & 255) / 255, (n & 255) / 255];
}

function engagementCeilingKmOf(e: EntityDto): number {
  const v = e.attrs['ceiling_km'];
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  if (typeof v === 'string' && /^\d+(\.\d+)?$/.test(v)) return Number(v);
  // Fall back to the type's schema default (so a placed unit lofts to its real
  // ceiling before anyone edits it), then the module default.
  const props = (getRegistry()?.types.find((t) => t.key === e.type)?.attrSchema as
    { properties?: Record<string, { default?: unknown }> } | undefined)?.properties;
  const d = props?.['ceiling_km']?.default;
  if (typeof d === 'number' && Number.isFinite(d) && d > 0) return d;
  return DEFAULT_CEILING_KM;
}

// Shared topology for the (RINGS+1)×(SEGS+1) hemisphere grid.
function buildIndices(): { tris: Uint16Array; lines: Uint16Array; caps: Uint16Array } {
  const at = (i: number, j: number) => i * (SEGS + 1) + j;
  const tris: number[] = [];
  for (let i = 0; i < RINGS; i++) {
    for (let j = 0; j < SEGS; j++) {
      tris.push(at(i, j), at(i, j + 1), at(i + 1, j),
                at(i, j + 1), at(i + 1, j + 1), at(i + 1, j));
    }
  }
  const lines: number[] = [];
  // Meridians: full apex->arc->apex outlines at a handful of azimuths — these
  // draw the lobe's silhouette.
  const mStep = Math.max(1, Math.round(SEGS / GRID_MERIDIANS));
  for (let j = 0; j <= SEGS; j += mStep) {
    for (let i = 0; i < RINGS; i++) lines.push(at(i, j), at(i + 1, j));
  }
  // Latitude arcs: a few range rings up the lobe. Skip the apex rings (0 and
  // RINGS), where the circle collapses onto the sensor point.
  for (let l = 1; l <= GRID_LAT_ARCS; l++) {
    const i = Math.round((l / (GRID_LAT_ARCS + 1)) * RINGS);
    if (i <= 0 || i >= RINGS) continue;
    for (let j = 0; j < SEGS; j++) lines.push(at(i, j), at(i, j + 1));
  }
  // Partial-azimuth shells need their two radial faces closed. The sampled
  // cross-section begins/ends on the sensor axis, so a fan triangulates each
  // face without adding vertices. Full rotations deliberately skip this IBO.
  const caps: number[] = [];
  for (let i = 1; i < RINGS - 1; i++) {
    caps.push(at(0, 0), at(i + 1, 0), at(i, 0));
    caps.push(at(0, SEGS), at(i, SEGS), at(i + 1, SEGS));
  }
  return { tris: new Uint16Array(tris), lines: new Uint16Array(lines),
    caps: new Uint16Array(caps) };
}

// Fill VPD*3 absolute-mercator xyz for one revolved shell into `out` at
// `vOffset`. The 2D cross-section is given as ground distance + altitude per
// ring (ring 0 = outer rim, ring RINGS = the axis), swept from azimuth `azStart`
// over `azSpan` radians. A full 2π sweep closes into a solid of revolution; a
// partial sweep is a wedge whose j=0 / j=SEGS meridians are its radial edges.
function fillRevolvedVerts(lon: number, lat: number, groundKm: number[],
                           altKm: number[], exagg: number, azStart: number,
                           azSpan: number, out: Float32Array, vOffset: number,
                           physicalBaseKm = 0, displayBaseKm = 0): void {
  const R = 6371.0088;
  const lat1 = lat * Math.PI / 180, lon1 = lon * Math.PI / 180;
  const sinLat1 = Math.sin(lat1), cosLat1 = Math.cos(lat1);
  let k = vOffset * 3;
  for (let i = 0; i <= RINGS; i++) {
    const d = groundKm[i]! / R;                     // angular ground distance
    const altM = (displayBaseKm + (altKm[i]! - physicalBaseKm) * exagg) * 1000;
    const sinD = Math.sin(d), cosD = Math.cos(d);
    for (let j = 0; j <= SEGS; j++) {
      const brg = azStart + azSpan * (j / SEGS);
      const lat2 = Math.asin(sinLat1 * cosD + cosLat1 * sinD * Math.cos(brg));
      const lon2 = lon1 + Math.atan2(Math.sin(brg) * sinD * cosLat1,
                                     cosD - sinLat1 * Math.sin(lat2));
      const m = maplibregl.MercatorCoordinate.fromLngLat(
        [lon2 * 180 / Math.PI, lat2 * 180 / Math.PI], altM);
      out[k++] = m.x; out[k++] = m.y; out[k++] = m.z;
    }
  }
}

// Engagement volume: a quarter-ellipse dome — max reach at the ground tapering
// to the ceiling overhead. A serviceable stand-in for a SAM's reach envelope.
function ellipsoidProfile(rangeKm: number, ceilKm: number):
    { ground: number[]; alt: number[] } {
  const ground: number[] = [], alt: number[] = [];
  for (let i = 0; i <= RINGS; i++) {
    const phi = (i / RINGS) * (Math.PI / 2);
    ground.push(rangeKm * Math.cos(phi));
    alt.push(ceilKm * Math.sin(phi));
  }
  return { ground, alt };
}

// --- module state -----------------------------------------------------------

interface DomeMeta {
  systemId: string; kind: 'radar' | 'engage'; vOffset: number;
  r: number; g: number; b: number; fillA: number; gridA: number;
  width: number; sector: boolean;
}

const IDX = buildIndices();
let domeMeta: DomeMeta[] = [];
let vertexData = new Float32Array(0);
let bufferDirty = false;
let visible = false;
let showRadar = true;
let showEngage = true;
let highlightId: string | null = null;
let focusId: string | null = null;
const siteElevationCacheKm = new Map<string, number>();
const siteElevationPending = new Map<string, Promise<void>>();
const siteElevationFailed = new Set<string>();
let elevationRefreshTimer: number | undefined;

interface DomeOptions {
  on: boolean;
  exaggeration: number;
  showRadar: boolean;
  showEngage: boolean;
}

let latestDomeRequest: { map: maplibregl.Map; entities: EntityDto[];
  opts: DomeOptions } | null = null;

function requestSiteElevation(lon: number, lat: number, key: string): void {
  if (siteElevationCacheKm.has(key) || siteElevationPending.has(key)
      || siteElevationFailed.has(key)) return;
  const task = fetchSiteElevationKm(lon, lat).then((elevationKm) => {
    siteElevationCacheKm.set(key, elevationKm);
    if (!visible || !latestDomeRequest) return;
    if (elevationRefreshTimer !== undefined) window.clearTimeout(elevationRefreshTimer);
    elevationRefreshTimer = window.setTimeout(() => {
      elevationRefreshTimer = undefined;
      const latest = latestDomeRequest;
      if (visible && latest) updateDomes(latest.map, latest.entities, latest.opts);
    }, 80);
  }).catch(() => {
    // Offline/CORS failure leaves the documented sea-level planning fallback;
    // an explicit site_elevation_km remains authoritative and needs no fetch.
    siteElevationFailed.add(key);
  }).finally(() => siteElevationPending.delete(key));
  siteElevationPending.set(key, task);
}

// --- the custom layer + interaction -----------------------------------------

const VERT = `
uniform mat4 u_matrix;
attribute vec3 a_pos;
void main() { gl_Position = u_matrix * vec4(a_pos, 1.0); }`;
const FRAG = `
precision mediump float;
uniform vec4 u_color;
void main() { gl_FragColor = vec4(u_color.rgb * u_color.a, u_color.a); }`;

function compile(gl: WebGLRenderingContext, src: string, type: number): WebGLShader {
  const s = gl.createShader(type)!;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.error('[domes] shader compile failed:', gl.getShaderInfoLog(s));
  }
  return s;
}

export interface DomeHooks { onSelect?: (systemId: string) => void }

export function installDomeLayer(map: maplibregl.Map, hooks: DomeHooks = {}): void {
  if (map.getLayer(LAYER_ID)) return;
  let program: WebGLProgram | null = null;
  let vbo: WebGLBuffer | null = null, triIbo: WebGLBuffer | null = null,
      lineIbo: WebGLBuffer | null = null, capIbo: WebGLBuffer | null = null;
  let uMatrix: WebGLUniformLocation | null = null, uColor: WebGLUniformLocation | null = null;
  let aPos = 0;

  const layer: maplibregl.CustomLayerInterface = {
    id: LAYER_ID, type: 'custom', renderingMode: '3d',
    onAdd(_m, gl) {
      program = gl.createProgram()!;
      gl.attachShader(program, compile(gl, VERT, gl.VERTEX_SHADER));
      gl.attachShader(program, compile(gl, FRAG, gl.FRAGMENT_SHADER));
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        console.error('[domes] program link failed:', gl.getProgramInfoLog(program));
      }
      aPos = gl.getAttribLocation(program, 'a_pos');
      uMatrix = gl.getUniformLocation(program, 'u_matrix');
      uColor = gl.getUniformLocation(program, 'u_color');
      vbo = gl.createBuffer();
      triIbo = gl.createBuffer();
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, triIbo);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, IDX.tris, gl.STATIC_DRAW);
      lineIbo = gl.createBuffer();
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, lineIbo);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, IDX.lines, gl.STATIC_DRAW);
      capIbo = gl.createBuffer();
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, capIbo);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, IDX.caps, gl.STATIC_DRAW);
    },
    render(gl, args) {
      if (!visible || !domeMeta.length || !program) return;
      // This custom shader currently consumes Mercator world coordinates. Do
      // not feed them through the globe matrix; MapLibre's native 2D footprint
      // remains available until a projection-aware custom shader is added.
      // getProjection() is undefined until a projection is explicitly set (the
      // default Mercator map never sets one) — treat that as "not globe" and
      // render, or this throws every frame and the shells never paint at all.
      if (map.getProjection()?.type === 'globe') return;
      gl.useProgram(program);
      gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
      if (bufferDirty) { gl.bufferData(gl.ARRAY_BUFFER, vertexData, gl.DYNAMIC_DRAW); bufferDirty = false; }
      gl.enableVertexAttribArray(aPos);
      gl.uniformMatrix4fv(uMatrix, false,
        new Float32Array((args as unknown as { defaultProjectionData: { mainMatrix: number[] } })
          .defaultProjectionData.mainMatrix));
      // Premultiplied translucent shells. Keep depth testing against the map so
      // the far/lower cage does not shine through terrain; do not write depth,
      // allowing overlapping planning volumes to remain readable.
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      gl.enable(gl.DEPTH_TEST);
      gl.depthMask(false);
      gl.disable(gl.CULL_FACE);
      for (const d of domeMeta) {
        if (d.kind === 'radar' ? !showRadar : !showEngage) continue;
        if (focusId && d.systemId !== focusId) continue;
        const boost = d.systemId === highlightId ? 1.9 : 1;
        gl.vertexAttribPointer(aPos, 3, gl.FLOAT, false, 0, d.vOffset * 3 * 4);
        if (d.fillA > 0) {
          gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, triIbo);
          gl.uniform4f(uColor, d.r, d.g, d.b, Math.min(1, d.fillA * boost));
          gl.drawElements(gl.TRIANGLES, IDX.tris.length, gl.UNSIGNED_SHORT, 0);
          if (d.sector) {
            gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, capIbo);
            gl.drawElements(gl.TRIANGLES, IDX.caps.length, gl.UNSIGNED_SHORT, 0);
          }
        }
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, lineIbo);
        gl.lineWidth(d.width);   // best-effort; most GPUs clamp to 1px
        gl.uniform4f(uColor, d.r, d.g, d.b, Math.min(1, d.gridA * boost));
        gl.drawElements(gl.LINES, IDX.lines.length, gl.UNSIGNED_SHORT, 0);
      }
      gl.enable(gl.DEPTH_TEST);
      gl.depthMask(true);
    },
    onRemove(_m, gl) {
      if (program) gl.deleteProgram(program);
      for (const b of [vbo, triIbo, lineIbo, capIbo]) if (b) gl.deleteBuffer(b);
    },
  };
  map.addLayer(layer);

  // Pickable footprints + a faint ground tint (also the picking geometry).
  map.addSource(PICK_SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  map.addLayer({
    id: PICK_FILL, type: 'fill', source: PICK_SRC,
    paint: { 'fill-color': ['coalesce', ['get', 'color'], '#55d6be'] as never, 'fill-opacity': 0.05 },
  });

  const popup = new maplibregl.Popup({ closeButton: false, closeOnClick: false,
    maxWidth: '240px', className: 'dome-tip' });
  // Through the shared hover pass (hover.ts): the two layer-scoped listeners
  // this replaces each ran their own feature query on every mouse move.
  registerHover(map, {
    layers: [PICK_FILL],
    cursor: 'pointer',
    onMove: (f, e) => {
      const p = f.properties as Record<string, unknown>;
      setHighlight(map, String(p.systemId));
      popup.setLngLat(e.lngLat).setHTML(tipHtml(p)).addTo(map);
    },
    onLeave: () => {
      setHighlight(map, null);
      popup.remove();
    },
  });
  map.on('click', PICK_FILL, (e) => {
    if (isMapEditing()) return;
    const f = e.features?.[0];
    if (!f) return;
    const id = String((f.properties as Record<string, unknown>).systemId);
    if ((e.originalEvent as MouseEvent).shiftKey) setFocus(map, focusId === id ? null : id);
    else hooks.onSelect?.(id);
  });
  // Esc clears an isolate.
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && focusId) setFocus(map, null);
  });
}

function esc(v: unknown): string {
  return String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function tipHtml(p: Record<string, unknown>): string {
  const km = (v: unknown) => (typeof v === 'number'
    ? `${v < 10 ? Math.round(v * 10) / 10 : Math.round(v)} km` : '—');
  const rows: string[] = [];
  if (p.engageKm != null) rows.push(`<b>Engagement</b> ${km(p.engageKm)}`);
  if (p.radarKm != null) rows.push(`<b>Reference slant</b> ${km(p.radarKm)}`);
  if (typeof p.instrumentedKm === 'number' && p.instrumentedKm !== p.radarKm) {
    rows.push(`<b>Instrumented</b> ${km(p.instrumentedKm)}`);
  }
  if (p.sensorAltitudeKm != null) rows.push(`<b>Sensor altitude</b> ${km(p.sensorAltitudeKm)}`);
  if (typeof p.minRangeKm === 'number' && p.minRangeKm > 0) {
    rows.push(`<b>Near-field blind range</b> ${km(p.minRangeKm)}`);
  }
  rows.push(`<b>Ceiling</b> ${km(p.ceilingKm)}`);
  if (typeof p.lowReachKm === 'number' && typeof p.lowAglM === 'number') {
    rows.push(`<b>${Math.round(p.lowAglM)} m AGL target</b> ${km(p.lowReachKm)} horizon/range`);
  }
  if (typeof p.azimuthDeg === 'number' && p.azimuthDeg < 360) {
    rows.push(`<b>Sector</b> ${p.azimuthDeg}°`);
  }
  if (typeof p.minElevDeg === 'number' && typeof p.maxElevDeg === 'number') {
    rows.push(`<b>Elevation</b> ${p.minElevDeg}°…${p.maxElevDeg}°`);
  }
  return `<div class="dome-tip-body">
    <div class="dome-tip-name">${esc(p.name) || 'Sensor coverage'}</div>
    <div class="dome-tip-rows">${rows.join(' · ')}</div>
    <div class="dome-tip-status">${esc(p.platform) ? `${esc(p.platform)} · ` : ''}${esc(p.status) || 'unknown'}</div>
    <div class="dome-tip-hint">click to open · shift-click to isolate</div></div>`;
}

// --- public API -------------------------------------------------------------

/** Rebuild sensor/engagement volumes. Radar geometry comes from the pure
 *  evaluator; only air-defence engagement ranges receive the legacy nominal
 *  ellipsoid (artillery and ship operating range are not SAM volumes). */
export function updateDomes(map: maplibregl.Map, entities: EntityDto[],
                            opts: DomeOptions): number {
  latestDomeRequest = { map, entities, opts };
  visible = opts.on;
  showRadar = opts.showRadar;
  showEngage = opts.showEngage;
  const registry = getRegistry();
  const ranged = entities.map((entity) => {
    const type = registry?.types.find((t) => t.key === entity.type);
    const radar = radarModelOf(entity);
    const engage = type?.category === 'air_defence' ? rangeKmOf(entity) : null;
    return { entity, type, radar, engage };
  }).filter((item) => item.entity.geometry?.type === 'Point'
    && (item.radar !== null || item.engage !== null));

  const meta: DomeMeta[] = [];
  const pick: GeoJSON.Feature[] = [];
  if (opts.on) {
    let count = 0;
    for (const item of ranged) {
      count += (item.radar ? 1 : 0) + (item.engage !== null ? 1 : 0);
    }
    vertexData = new Float32Array(count * VPD * 3);
    let vOff = 0;
    for (const { entity: e, radar, engage } of ranged) {
      const [lon, lat] = (e.geometry as unknown as { coordinates: [number, number] }).coordinates;
      const rs = volumeStyle.radar, es = volumeStyle.engage;
      const [rr, rg, rb] = hexRgb(rs.color);    // detection shell
      const [er, eg, eb] = hexRgb(es.color);    // engagement shell
      const status = String(e.attrs.status ?? 'unknown');
      const statusFactor = status === 'offline' ? 0.25 : status === 'limited' ? 0.65 : 1;
      const queriedTerrainM = map.queryTerrainElevation([lon, lat]);
      const displayedTerrainKm = Math.max(0, (queriedTerrainM ?? 0) / 1000);
      const terrainExaggeration = Math.max(0.01, map.getTerrain()?.exaggeration ?? 1);
      const queriedPhysicalTerrainKm = queriedTerrainM == null
        ? null : displayedTerrainKm / terrainExaggeration;
      const elevationKey = `${lon.toFixed(6)},${lat.toFixed(6)}`;
      if (queriedPhysicalTerrainKm !== null) {
        siteElevationCacheKm.set(elevationKey, queriedPhysicalTerrainKm);
      }

      // Radar first (usually larger, underneath engagement in the pick stack).
      if (radar) {
        const { spec, pose, effectiveEarthK } = radar;
        // Ground/naval/aerostat values are height above their local surface.
        // Promote them to the spherical datum when the active DEM can answer;
        // airborne telemetry is already MSL/datum altitude.
        const usesSurfaceBase = spec.platform !== 'airborne';
        if (usesSurfaceBase && radar.siteElevationKm == null
            && queriedPhysicalTerrainKm === null
            && !siteElevationCacheKm.has(elevationKey)) {
          requestSiteElevation(lon, lat, elevationKey);
        }
        const terrainKm = usesSurfaceBase
          ? radar.siteElevationKm ?? queriedPhysicalTerrainKm
            ?? siteElevationCacheKm.get(elevationKey) ?? 0
          : 0;
        const sensorAltitudeKm = pose.altitudeKm + terrainKm;
        // The analytic lobe's altitudes are height above the sensor (apex = 0),
        // so exaggeration measures from the ground (physical base 0) and the
        // apex is dropped onto the displayed terrain — same convention as the
        // engagement ellipsoid below.
        const profile = coverageShapeProfile({
          peakRangeKm: spec.referenceRangeKm,
          peakElevationDeg: RANGE_PEAK_ELEV_DEG,
          rangeExponent: RANGE_ELEV_EXPONENT,
          minElevationDeg: Math.max(spec.minElevationDeg, LOW_FADE_ELEV_DEG),
          maxElevationDeg: spec.maxElevationDeg,
        }, COVERAGE_ARC_SAMPLES);
        const facing = normalizeBearing(spec.bearingDeg + (pose.headingDeg ?? 0));
        const sector = spec.azimuthDeg < 360;
        const azSpan = sector ? spec.azimuthDeg * Math.PI / 180 : 2 * Math.PI;
        const azStart = sector ? (facing - spec.azimuthDeg / 2) * Math.PI / 180 : 0;
        fillRevolvedVerts(lon, lat, profile.ground, profile.altitude,
          opts.exaggeration, azStart, azSpan, vertexData, vOff,
          0, displayedTerrainKm);
        meta.push({ systemId: e.id, kind: 'radar', vOffset: vOff,
          r: rr, g: rg, b: rb,
          fillA: (rs.fill ? rs.fillOpacity : 0) * statusFactor,
          gridA: rs.lineOpacity * statusFactor, width: rs.lineWidth, sector });
        vOff += VPD;
        const footprint = geodesicSector(lon, lat, profile.maxGroundRangeKm,
          spec.azimuthDeg, facing);
        const lowAglM = 50;
        const lowAltitudeKm = terrainKm + lowAglM / 1000;
        pick.push({ type: 'Feature', properties: {
          systemId: e.id, name: e.name ?? '', color: rs.color, kind: 'radar', status,
          radarKm: spec.referenceRangeKm,
          instrumentedKm: spec.instrumentedRangeKm,
          ceilingKm: spec.targetCeilingKm,
          sensorAltitudeKm,
          minRangeKm: spec.minRangeKm ?? 0,
          platform: spec.platform,
          azimuthDeg: spec.azimuthDeg,
          minElevDeg: spec.minElevationDeg,
          maxElevDeg: spec.maxElevationDeg,
          lowAglM,
          lowReachKm: groundReachAtAltitudeKm(spec, sensorAltitudeKm,
            lowAltitudeKm, effectiveEarthK),
        }, geometry: { type: 'Polygon', coordinates: [footprint] } });
      }

      if (engage !== null) {
        const ceilKm = engagementCeilingKmOf(e);
        const profile = ellipsoidProfile(engage, ceilKm);
        fillRevolvedVerts(lon, lat, profile.ground, profile.alt,
          opts.exaggeration, 0, 2 * Math.PI, vertexData, vOff,
          0, displayedTerrainKm);
        meta.push({ systemId: e.id, kind: 'engage', vOffset: vOff,
          r: er, g: eg, b: eb,
          fillA: (es.fill ? es.fillOpacity : 0) * statusFactor,
          gridA: es.lineOpacity * statusFactor, width: es.lineWidth, sector: false });
        vOff += VPD;
        pick.push({ type: 'Feature', properties: {
          systemId: e.id, name: e.name ?? '', color: es.color, kind: 'engage', status,
          engageKm: engage, ceilingKm: ceilKm,
        }, geometry: { type: 'Polygon', coordinates: [geodesicCircle(lon, lat, engage)] } });
      }
    }
    bufferDirty = true;
  } else {
    vertexData = new Float32Array(0);
  }
  domeMeta = meta;
  (map.getSource(PICK_SRC) as maplibregl.GeoJSONSource | undefined)
    ?.setData({ type: 'FeatureCollection', features: pick });
  if (!opts.on) { highlightId = null; focusId = null; }
  map.triggerRepaint();
  return ranged.length;
}

/** Whether the 3D sensor volumes are currently shown — the selection card uses
 *  this to skip drawing redundant 2D rings for a unit whose coverage is already
 *  on screen as a volume. */
export function domesVisible(): boolean {
  return visible;
}

function setHighlight(map: maplibregl.Map, id: string | null): void {
  if (highlightId === id) return;
  highlightId = id;
  map.triggerRepaint();
}

function setFocus(map: maplibregl.Map, id: string | null): void {
  focusId = id;
  map.triggerRepaint();
}
